import { ListFilter } from "lucide-react";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { useIsMobile } from "@/hooks/use-mobile";

import { FilterCategory } from "../filters/FilterCategory";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CustomFieldDefinition, CustomFieldResource } from "../types";

const isListField = (field: CustomFieldDefinition) =>
  (field.type === "select" || field.type === "multi_select") &&
  (field.options?.length ?? 0) > 0;

/**
 * PostgREST filter on one option of a custom field, stored in the
 * `custom_fields` JSON column. The FakeRest adapter understands it too.
 */
const customFieldFilter = (
  field: CustomFieldDefinition,
  value: string,
): Record<string, string> =>
  field.type === "multi_select"
    ? { [`custom_fields->${field.key}@cs`]: JSON.stringify([value]) }
    : { [`custom_fields->>${field.key}@eq`]: value };

/**
 * One filter section per list custom field created in the Settings, for
 * example an age range: one button per option.
 */
export const CustomFieldFilters = ({
  resource,
}: {
  resource: CustomFieldResource;
}) => {
  const { customFields } = useConfigurationContext();
  const isMobile = useIsMobile();
  const fields = (customFields?.[resource] ?? []).filter(isListField);

  return fields.map((field) => (
    <FilterCategory
      key={field.key}
      icon={<ListFilter className="h-4 w-4" />}
      label={field.label}
    >
      {field.options?.map((option) => (
        <ToggleFilterButton
          key={option.value}
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={option.label}
          value={customFieldFilter(field, option.value)}
          size={isMobile ? "lg" : undefined}
        />
      ))}
    </FilterCategory>
  ));
};
